import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import { errorFetchingAuthsToView, getAdminAndUserAuthsResposneForView, isFetchingAuthsToView } from 'src/app/store/createManageUser/selector';
import { IAdminAndUserAuthsReq, ICreateManageUserState, IManageuser, initialCreateManageUserState } from 'src/app/store/createManageUser/state';
import { Lux } from '@roosevelt/common-ui-lib/core';

@Component({
    selector: 'bmt-view-auth-at-group',
    templateUrl: './view-auth-at-group-level.component.html',
    styleUrls: ['./view-auth-at-group-level.component.scss'],
    standalone: false
})
export class ViewAuthAtGroupComponent implements OnInit, OnDestroy {
  luxId = 'BMT-view-auth-at-group' + this.constructor.name + new Date().toISOString();
  private readonly destroyRef = inject(DestroyRef);
  readonly userContext = input('');
  readonly selectedUserId = input('');
  readonly userInfo = input<IManageuser | undefined>(undefined);
  readonly errors = toSignal(this.lux.get(errorFetchingAuthsToView, this.luxId), { initialValue: '' });
  readonly fetchingAuths = toSignal(this.lux.get(isFetchingAuthsToView, this.luxId), { initialValue: false });
  readonly userAndAdminAuths = toSignal(this.lux.get(getAdminAndUserAuthsResposneForView, this.luxId), {
    initialValue: initialCreateManageUserState.viewAdminAndUserAuths.authResponse
  });
  readonly totalAuths = computed(() => this.userAndAdminAuths()?.pagination?.totalRecords || 0);
  defaultPageSize = 100;
  readonly adminSubGroupAuths = computed(() => {
    const adminAndUserAuths = this.userAndAdminAuths();
    const userRelatedAuthorizations = adminAndUserAuths?.userRelatedAuthorizations || [];
    const adminRole = this.userInfo()?.adminRole;

    if (!adminRole || !userRelatedAuthorizations.some(auths => auths.subGroupSpecifiedIdentifier)) {
      return {} as Record<string, any>;
    }

    const objectToConsider = !this.singleClientOrSubGroupAdmins.includes(adminRole)
      ? adminAndUserAuths.adminRelatedAuthorizations?.subGroupAuthorizations || []
      : userRelatedAuthorizations;

    return this.createAdminSubGrpAuthsObj(objectToConsider, adminRole, adminAndUserAuths);
  });
  singleClientOrSubGroupAdmins = ['BMT-CLIENT-ASSOCIATION-ADMIN', 'BMT-CLIENT-ASSOCIATION-DELEGATE', 'BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN', 'BMT-GROUP-DELEGATE', 'BMT-SUBGROUP-DELEGATE'];

  constructor(private readonly lux: Lux<{ createManageUserState: ICreateManageUserState }>, private readonly createManageUserAsync: CreateManageUserAsync) {
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }

  ngOnInit(): void {
    const initailState = initialCreateManageUserState.viewAdminAndUserAuths.authResponse;
    this.lux.set(state => state.createManageUserState.viewAdminAndUserAuths, {
      authResponse: initailState
    });
    this.fetchUserAuthorizations(0);
  }

  createAdminSubGrpAuthsObj(authsToConsider = [], adminRole = '', adminAndUserAuths = this.userAndAdminAuths()) {
    return authsToConsider.length ? authsToConsider.reduce((bag, key) => ({
      ...bag,
      [key.subGroupSpecifiedIdentifier]: !this.singleClientOrSubGroupAdmins.includes(adminRole) ? key : adminAndUserAuths.adminRelatedAuthorizations
    }), {}) : {};
  }

  fetchUserAuthorizations(offset: number): void {
    if (!this.userContext()) {
      return;
    }

    const userContextStr = this.userContext().split('/');
    if (userContextStr.length < 3) {
      return;
    }

    const adminAndUserAuthReq: IAdminAndUserAuthsReq = {
      userIdentifier: this.selectedUserId() || '',
      payer: userContextStr[1],
      payerAdmin: userContextStr[0],
      groupAccessLevel: 'one',
      groupSpecifiedIdentifier: userContextStr[2],
      ...userContextStr[3] && { subGroupSpecifiedIdentifier: userContextStr[3] },
      pagination: {
        offset: offset > this.totalAuths() ? (offset - this.totalAuths()) : offset,
        limit: this.defaultPageSize
      },
      sort: [
        {
          fieldName: 'specifiedId',
          order: 'ASC'
        }
      ]
    };
    this.lux.set(state => state.createManageUserState.viewAdminAndUserAuths, { authRequest: adminAndUserAuthReq });
    this.createManageUserAsync.getAdminAndUserAuths('view')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getMoreAuths(page) {
    this.fetchUserAuthorizations(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }
}
